import { useMemo } from 'react'

import { MCP_TECHNOLOGY_ID } from '@/core/const/mcpGateway.const'
import type { UseInstallationConfigParams } from '@/core/types/installationConfig.type'

import { getPackageManager } from './constants'
import {
  buildConfigurationMap,
  buildImportCode,
  buildMcpClientConfig,
  buildRegistry,
  buildSetupCommandMap,
} from './mappers'

export function useInstallationConfig(params: UseInstallationConfigParams) {
  const {
    selectedTechnology,
    libraryData,
    classData,
    gatewayPath,
    gatewayWsPort,
    gatewayMcpPort,
    guid,
    gsmuEndpoint,
    registry: registryPath,
    isSecured,
    projectName,
    technologySetupCommands,
    technologyConfigurations,
    technologyImports,
    technologyLibraryImports,
  } = params

  const packageManager = useMemo(() => getPackageManager(libraryData?.runtimeName), [libraryData?.runtimeName])

  const registry = useMemo(() => {
    if (!selectedTechnology || selectedTechnology === MCP_TECHNOLOGY_ID) return ''
    if (!gsmuEndpoint?.trim() || !registryPath?.trim() || !guid?.trim()) {
      return ''
    }

    return buildRegistry({
      selectedTechnology,
      gsmuEndpoint,
      registryPath,
      guid,
    })
  }, [selectedTechnology, gsmuEndpoint, registryPath, guid])

  const setupCommandMap = useMemo(() => {
    if (selectedTechnology === MCP_TECHNOLOGY_ID) return {}

    return buildSetupCommandMap({
      selectedTechnology,
      technologySetupCommands,
      libraryData,
      registry,
      packageManager,
    })
  }, [selectedTechnology, technologySetupCommands, libraryData, registry, packageManager])

  const configurationMap = useMemo(() => {
    if (selectedTechnology === MCP_TECHNOLOGY_ID) {
      return {
        'MCP client config': buildMcpClientConfig({
          gatewayPath,
          gatewayMcpPort,
          isSecured,
          projectName,
        }),
      }
    }

    return buildConfigurationMap({
      selectedTechnology,
      technologyConfigurations,
      isSecured,
      gatewayPath,
      gatewayWsPort,
      libraryData,
      packageManager,
    })
  }, [
    selectedTechnology,
    technologyConfigurations,
    isSecured,
    gatewayPath,
    gatewayWsPort,
    gatewayMcpPort,
    libraryData,
    packageManager,
    projectName,
  ])

  const importCode = useMemo(() => {
    if (selectedTechnology === MCP_TECHNOLOGY_ID) return ''

    return buildImportCode({
      selectedTechnology,
      technologyImports,
      technologyLibraryImports,
      libraryData,
      classData,
      packageManager,
    })
  }, [selectedTechnology, technologyImports, technologyLibraryImports, libraryData, classData, packageManager])

  return {
    setupCommandMap,
    configurationMap,
    importCode,
  }
}
